import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { setPendingAppointments } from "../../../src/redux/appointmentsSlice";
import API from "../../utils/customAxios";
import { toast } from "react-toastify";
import notificationSound from "../../assets/audio/appointment-notification.mp3";

export default function GlobalNotifications() {
  const dispatch = useDispatch();
  const location = useLocation();
  const audioRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const shownRef = useRef(false);
  const audioUnlockedRef = useRef(false);

  const isAppointmentRoute = "/appointment"

  // initialize audio element only once
  useEffect(() => {
    audioRef.current = new Audio(notificationSound);
    audioRef.current.preload = "auto";
  }, []);

  // show modal on initial mount (every reload) unless we're on appointment route
  useEffect(() => {
    if (!shownRef.current && !location.pathname.includes(isAppointmentRoute)) {
      // slight delay so UI settles and user sees a nice entrance
      setTimeout(() => {
        setIsModalOpen(true);
        shownRef.current = true;
      }, 350);
    }
    // do NOT re-open on route changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // small helper to unlock audio by user interaction
  const unlockAudio = async () => {
    if (!audioRef.current || audioUnlockedRef.current) return;
    try {
      // Attempt to play & immediately pause to satisfy autoplay policies
      await audioRef.current.play();
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioUnlockedRef.current = true;
    } catch (err) {
      // ignore; some browsers still block until specific gesture
      console.warn("Audio unlock attempt failed:", err);
    }
  };

  useEffect(() => {
  const onFirstUserInteraction = async () => {
    if (audioUnlockedRef.current) return;

    try {
      await unlockAudio();
    } finally {
      window.removeEventListener("click", onFirstUserInteraction);
      window.removeEventListener("keydown", onFirstUserInteraction);
      window.removeEventListener("touchstart", onFirstUserInteraction);
    }
  };

  window.addEventListener("click", onFirstUserInteraction, { once: true });
  window.addEventListener("keydown", onFirstUserInteraction, { once: true });
  window.addEventListener("touchstart", onFirstUserInteraction, { once: true });

  return () => {
    window.removeEventListener("click", onFirstUserInteraction);
    window.removeEventListener("keydown", onFirstUserInteraction);
    window.removeEventListener("touchstart", onFirstUserInteraction);
  };
}, []);


  const handleEnableSound = async () => {
    // await unlockAudio();
    setIsModalOpen(false);
  };

  // keep audio stopped/paused automatically on appointment route
  useEffect(() => {
    if (location.pathname.includes(isAppointmentRoute)) {
      try {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
      } catch (e) {
        // ignore
      }
    }
  }, [location.pathname]);

  const pathnameRef = useRef(location.pathname);

useEffect(() => {
  pathnameRef.current = location.pathname;
}, [location.pathname]);

  // -------------------- existing polling logic --------------------
useEffect(() => {
  let cancelled = false;

  const fetchPendingAppointments = async () => {
    try {
      const response = await API.get("/doctor/pending-appointment");
      const appointments = response?.data?.data || [];

      if (response?.data?.code === 200) {
        dispatch(setPendingAppointments(appointments));
      }

      if (appointments.length > 0) {
        const isOnAppointmentRoute =
          pathnameRef.current.includes(isAppointmentRoute);

        // 🔔 sound
        if (!isOnAppointmentRoute && audioRef.current) {
          if (audioUnlockedRef.current) {
            try {
              audioRef.current.currentTime = 0;
              await audioRef.current.play();
            } catch (err) {
              console.error("Audio play blocked:", err);
            }
          }
        }

        // 🔔 toast
        if (!document.hidden && !isOnAppointmentRoute) {
          toast.info("You have pending appointments", {
            position: "top-right",
            autoClose: 4000,
          });
        }
      }
    } catch (err) {
      if (!cancelled) console.error("Notification API error:", err);
    }
  };

  fetchPendingAppointments();
  const interval = setInterval(fetchPendingAppointments, 15000);

  return () => {
    cancelled = true;
    clearInterval(interval);
  };
}, []); // ✅ NO pathname here

   // -------------------- modal UI --------------------
  const backdropStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(11,12,17,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    backdropFilter: "blur(4px)",
    padding: 20,
  };

  const modalStyle = {
    width: 420,
    maxWidth: "94%",
    borderRadius: 14,
    padding: "18px 18px 20px 18px",
    boxShadow: "0 20px 40px rgba(2,6,23,0.15)",
    background: "#ffffff",
    color: "#0b1220",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    alignItems: "stretch",
    animation: "popIn 320ms cubic-bezier(.2,.9,.2,1)",
  };

  const headerRow = {
    display: "flex",
    gap: 12,
    alignItems: "center",
  };

  const iconWrap = {
    width: 56,
    height: 56,
    borderRadius: 12,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(180deg,#f8fafc,#eef2ff)",
    border: "1px solid rgba(12,17,38,0.06)",
    boxShadow: "0 6px 18px rgba(12,17,38,0.06)",
  };

  const titleStyle = { fontSize: 16, fontWeight: 700, margin: 0, color: "#0b1220" };
  const subtitleStyle = { fontSize: 13, lineHeight: 1.45, margin: 0, color: "#334155" };

  const divider = { height: 1, background: "#f1f5f9", borderRadius: 2, marginTop: 4 };

  const okButton = {
    marginTop: 6,
    width: "100%",
    padding: "12px 14px",
    borderRadius: 10,
    border: "none",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 15,
    background: "linear-gradient(135deg, #29BCC1 0%, #1aa3a8 100%)",
    color: "#fff",
    boxShadow: "0 8px 20px rgba(99,102,241,0.12)",
  };

  return (
    <>
      {/* Modal */}
      {isModalOpen && (
        <div style={backdropStyle} role="dialog" aria-modal="true" aria-label="Enable notification sound">
          <div style={modalStyle}>
            <div style={headerRow}>
              <div style={iconWrap} aria-hidden>
                {/* colored bell icon */}
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 17H9" stroke="#0b1220" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" opacity="0.95" />
                  <path d="M12 3v2" stroke="#0b1220" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" opacity="0.95" />
                  <path d="M6 8c0-3 1.5-5 6-5s6 2 6 5v4c0 2 1 3 1 3H5s1-1 1-3V8z" stroke="#0b1220" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" opacity="0.95" />
                </svg>
              </div>

              <div style={{ flex: 1 }}>
                <h3 style={titleStyle}>Enable notification sound</h3>
                <p style={subtitleStyle}>Tap "OK" so we can play alerts for new appointments. This helps you stay informed instantly when a new booking arrives.</p>
              </div>
            </div>

            <div style={divider} />

            <button
              style={okButton}
              onClick={handleEnableSound}
              aria-label="Enable notification sound"
              autoFocus
            >
              OK
            </button>
          </div>
        </div>
      )}

    </>
  );
}
